import { useEffect, useState } from "react"
import type { Catalogs, DateField, PersonRole } from "@shared/api"
import type { Option } from "@shared/catalogs"
import { RANGE_PRESETS, type RangePresetId, rangePreset } from "../utils/datetime"
import "../styles/filters.scss"

/**
 * UNA sola fila de filtros que scopea todo lo que hay debajo — nunca por gráfico,
 * nunca dentro de una card.
 *
 * Los catálogos NO se hardcodean acá: llegan de GET /api/dashboard/catalogs, que los
 * sirve desde src/config.ts. Así los dropdowns no pueden divergir ni del CHECK de la
 * base ni de lo que dice el DM de Slack.
 */

export interface RequestsFilterValue {
  status: string[]
  activity_type?: string
  format?: string
  frequency?: string
  person?: string
  person_role: PersonRole
  from?: string
  to?: string
  date_field: DateField
  q: string
  preset: RangePresetId
}

export const EMPTY_FILTER: RequestsFilterValue = {
  status: [],
  person_role: "any",
  date_field: "proposed_at",
  q: "",
  preset: "all",
}

export function isFilterDirty(f: RequestsFilterValue): boolean {
  return (
    f.status.length > 0 ||
    !!f.activity_type ||
    !!f.format ||
    !!f.frequency ||
    !!f.person ||
    f.person_role !== "any" ||
    f.date_field !== "proposed_at" ||
    !!f.from ||
    !!f.to ||
    f.q.trim() !== "" ||
    f.preset !== "all"
  )
}

// Exportados porque Fase 2 los reusa (toggle gráfico/tabla, selección de personas).
export function Chips({
  options,
  selected,
  onToggle,
}: {
  options: Option[]
  selected: string[]
  onToggle: (value: string) => void
}) {
  return (
    <div className="chip-row">
      {options.map((o) => {
        const active = selected.includes(o.value)
        return (
          <button
            key={o.value}
            type="button"
            className={active ? "chip active" : "chip"}
            aria-pressed={active}
            onClick={() => onToggle(o.value)}
          >
            {/* El check es el canal de RELIEVE: el estado activo no se distingue solo
                por color. */}
            {active && <span className="chip-check">✓</span>}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function Segmented<T extends string>({
  value,
  options,
  onChange,
}: {
  value: T
  options: { value: T; label: string }[]
  onChange: (v: T) => void
}) {
  return (
    <div className="segmented">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={o.value === value ? "active" : undefined}
          aria-pressed={o.value === value}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

export function RequestsFilterBar({
  value,
  onChange,
  onClear,
  catalogs,
  people,
  displayTimezone,
}: {
  value: RequestsFilterValue
  onChange: (patch: Partial<RequestsFilterValue>) => void
  onClear: () => void
  catalogs: Catalogs
  people: { slack_user_id: string; name: string }[]
  displayTimezone: string
}) {
  // El input de texto es local y se debouncea en la página: escribir no debe disparar
  // una request por tecla.
  const [text, setText] = useState(value.q)
  useEffect(() => setText(value.q), [value.q])

  function applyPreset(preset: RangePresetId) {
    // 'custom' conserva las fechas que el usuario ya tipeó: solo cambia la etiqueta del
    // select. Los demás presets se calculan en displayTimezone, NO en la zona del
    // browser — "últimos 30 días" tiene que dar los mismos YYYY-MM-DD para un admin en
    // Buenos Aires que para uno en Miami, porque esos bordes se comparan server-side.
    if (preset === "custom") {
      onChange({ preset })
      return
    }
    const { from, to } = rangePreset(preset, displayTimezone)
    onChange({ preset, from, to })
  }

  function toggleStatus(v: string) {
    onChange({
      status: value.status.includes(v)
        ? value.status.filter((s) => s !== v)
        : [...value.status, v],
    })
  }

  return (
    <div className="filter-bar">
      <div className="filter-group">
        <span className="filter-label">Estado</span>
        <Chips options={catalogs.requestStatuses} selected={value.status} onToggle={toggleStatus} />
      </div>

      <div className="filter-group">
        <span className="filter-label">Tipo de actividad</span>
        <Chips
          options={catalogs.activityTypes}
          selected={value.activity_type ? [value.activity_type] : []}
          // Single-select: volver a clickear el activo lo deselecciona.
          onToggle={(v) => onChange({ activity_type: value.activity_type === v ? undefined : v })}
        />
      </div>

      {/* El chip de formato SOLO se renderiza si INCLUDE_FORMAT está en true. Ojo: la
          COLUMNA se muestra igual siempre que no sea null, porque las filas históricas
          conservan su valor aunque después se apague el toggle. */}
      {catalogs.includeFormat && (
        <div className="filter-group">
          <span className="filter-label">Formato</span>
          <Chips
            options={catalogs.formats}
            selected={value.format ? [value.format] : []}
            onToggle={(v) => onChange({ format: value.format === v ? undefined : v })}
          />
        </div>
      )}

      <div className="filter-group">
        <span className="filter-label">Cadencia</span>
        <Chips
          options={catalogs.frequencies}
          selected={value.frequency ? [value.frequency] : []}
          onToggle={(v) => onChange({ frequency: value.frequency === v ? undefined : v })}
        />
      </div>

      <div className="filter-group">
        {/* Los dos rangos hacen falta de verdad: los reportes quieren proposed_at, y el
            SLA de "pendiente hace más de 24 h" quiere created_at. */}
        <span className="filter-label">Rango</span>
        <Segmented<DateField>
          value={value.date_field}
          options={[
            { value: "proposed_at", label: "Por fecha propuesta" },
            { value: "created_at", label: "Por fecha de solicitud" },
          ]}
          onChange={(v) => onChange({ date_field: v })}
        />
      </div>

      <div className="filter-group">
        <span className="filter-label">Período</span>
        <select
          className="filter-select"
          value={value.preset}
          onChange={(e) => applyPreset(e.target.value as RangePresetId)}
        >
          {RANGE_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
          <option value="custom">Personalizado</option>
        </select>
      </div>

      <div className="filter-group">
        <span className="filter-label">Desde / hasta</span>
        <div className="date-range">
          <input
            className="filter-input"
            type="date"
            value={value.from ?? ""}
            onChange={(e) => onChange({ from: e.target.value || undefined, preset: "custom" as RangePresetId })}
          />
          <span className="date-sep">→</span>
          <input
            className="filter-input"
            type="date"
            value={value.to ?? ""}
            onChange={(e) => onChange({ to: e.target.value || undefined, preset: "custom" as RangePresetId })}
          />
        </div>
      </div>

      <div className="filter-group">
        <span className="filter-label">Persona</span>
        <select
          className="filter-select"
          value={value.person ?? ""}
          onChange={(e) => onChange({ person: e.target.value || undefined })}
        >
          <option value="">Cualquiera</option>
          {people.map((p) => (
            <option key={p.slack_user_id} value={p.slack_user_id}>
              {p.name}
            </option>
          ))}
        </select>
        {value.person && (
          <Segmented<PersonRole>
            value={value.person_role}
            options={[
              { value: "any", label: "Cualquier rol" },
              { value: "requester", label: "Solicitante" },
              { value: "participant", label: "Participante" },
            ]}
            onChange={(v) => onChange({ person_role: v })}
          />
        )}
      </div>

      <div className="filter-group grow">
        <label htmlFor="q">Buscar en tema y justificación</label>
        <input
          id="q"
          className="filter-input"
          type="search"
          placeholder="Tema, palabra clave…"
          value={text}
          onChange={(e) => {
            setText(e.target.value)
            onChange({ q: e.target.value })
          }}
        />
      </div>

      {isFilterDirty(value) && (
        <div className="filter-actions">
          <button type="button" className="btn btn-ghost" onClick={onClear}>
            Limpiar filtros
          </button>
        </div>
      )}
    </div>
  )
}
